<template>
  <VglGroup
    :position-x="-horizontalOffset"
    :position-y="-verticalOffset"
  >
    <VglArrowHelper
      v-for="(vector, i) in vectors"
      :key="`vector${i}`"
      :position-x="vector.position.x"
      :position-y="vector.position.y"
      :position-z="vector.position.z"
      :direction-x="vector.direction.x"
      :direction-y="vector.direction.y"
      :direction-z="vector.direction.z"
      :length="vector.length"
      :head-width="headWidth"
      :head-length="headLength"
      :color="vector.color"
    >
      {{ vectors }}
    </VglArrowHelper>
  </VglGroup>
</template>

<script>
import * as d3 from 'd3'

import { 
  VglGroup,
  VglArrowHelper,
} from 'vue-gl';

import { getNorm } from '@js/utils/TrajectoryUtil.js';

export default {
  components: {
    VglGroup,
    VglArrowHelper,
  },
  props: {
    trajectory: {
      required: true,
    },
    verticalOffset: {
      default: 0,
    },
    horizontalOffset: {
      default: 0,
    },
    selection: {
      default: () => [],
    },
    division: {
      default: 10,
    },
    color: {
      default: 'bilateral',
    },
    vectorMode: {
      default: null,
    },
    headWidth: {
      default: 0.015,
    },
    headLength: {
      default: 0.03,
    },
    trunk: {
      default: null,
    }
  },
  computed: {
    isTrunk: function(){
      return this.trunk != null;
    },
    getColor: function(){
      if(this.color == 'phase'){
        return value => value
      }

      return value => {
        const v = (value - this.colorMinValue)/this.colorValueRange;
        return d3.interpolateSpectral(1 - v);
      };
    },
    colorValueRange: function(){
      switch(this.color){
        case 'position':
          return this.maxPosition - this.minPosition;
        case 'velocity':
          return this.maxVelocity - this.minVelocity;
        case 'acceleration':
          return this.maxAcceleration - this.minAcceleration;
      }
      return 1;
    },
    colorMinValue: function(){
      switch(this.color){
        case 'position':
          return this.minPosition;
        case 'velocity':
          return this.minVelocity;
        case 'acceleration':
          return this.minAcceleration;
      }
      return 0;
    },
    trajectoryPositions: function(){
      return this.trajectory.positions ?? this.trajectory;
    },
    positions: function(){
      const rtn = [];
      this.trajectoryPositions.forEach(p => {
        rtn.push(getNorm({
          x: p.x,
          y: p.y,
          z: p.z,
        }));
      });
      return rtn;
    },
    minPosition: function(){
      return Math.min(...this.positions);
    },
    maxPosition: function(){
      return Math.max(...this.positions);
    },
    velocities: function(){
      const rtn = [];
      let previousPoint = {
        x: 0,
        y: 0,
        z: 0
      }
      this.trajectoryPositions.forEach(p => {
        rtn.push(getNorm({
          x: p.x - previousPoint.x,
          y: p.y - previousPoint.y,
          z: p.z - previousPoint.z,
        }));

        previousPoint = p;
      });
      return rtn;
    },
    sortedVelocities: function(){
      const rtn = [...this.velocities];
      rtn.sort();
      return rtn;      
    },
    minVelocity: function(){
      return this.sortedVelocities[0];
    },
    maxVelocity: function(){
      return this.isTrunk
        ? this.sortedVelocities[parseInt(this.sortedVelocities.length - 2)]
        : this.sortedVelocities[parseInt(this.sortedVelocities.length - 1)];
    },
    accelerations: function(){
      const rtn = [];
      let previousPoint = {
        x: 0,
        y: 0,
        z: 0
      }
      let previousVelocity = {
        x: 0,
        y: 0,
        z: 0
      };
      this.trajectoryPositions.forEach(p => {
        const velocity = {
          x: p.x - previousPoint.x,
          y: p.y - previousPoint.y,
          z: p.z - previousPoint.z,
        }

        rtn.push(getNorm({
          x: velocity.x - previousVelocity.x,
          y: velocity.y - previousVelocity.y,
          z: velocity.z - previousVelocity.z,
        }));

        previousPoint = p;
        previousVelocity = velocity;
      });
      return rtn;
    },
    sortedAccelerations: function(){
      const rtn = [...this.accelerations];
      rtn.sort();
      return rtn;      
    },
    minAcceleration: function(){
      return this.sortedAccelerations[0];
    },
    maxAcceleration: function(){
      return this.isTrunk
        ? this.sortedAccelerations[this.sortedAccelerations.length - 3]
        : this.sortedAccelerations[this.sortedAccelerations.length - 1];
    },
    colorValues: function(){
      switch(this.color){
        case 'position':
          return this.positions;
        case 'velocity':
          return this.velocities;
        case 'acceleration':
          return this.accelerations;
        case 'phase':
          return this.trajectoryPositions.map(p => {
            switch(p.phase){
              case 'red':
                return '#FF6600'
              case 'blue':
                return '#0066FF'
              case 'green':
                return '#44CC44'
              default:
                return '#CCCC44'
            }
          });
      }
      return null;
    },
    arrowLength: function(){
      if(this.isTrunk){
        return 3.0;
      }

      switch(this.vectorMode){
        case 'position':
          return 2.0;
        case 'velocity':
          return 10.0;
        case 'acceleration':
          return 10.0;
      }

      return 1;
    },
    vectors: function(){
      if(!this.vectorMode){
        return [];
      }

      const rtn = [];
      const length = this.trajectoryPositions.length;
      const interval = length/this.division;
      let previousVelocity = {
        x: 0,
        y: 0,
        z: 0
      };
      for(let i=0; i < this.division; i++){
        const index = Math.round(interval*i);
        const nextIndex = Math.min(Math.round(interval*(i + 1)), length - 1);

        const position = this.trajectoryPositions[index];
        const nextPosition = this.trajectoryPositions[nextIndex];

        if(position.index < this.selection.from || this.selection.to < position.index){
          continue;
        }

        if(nextPosition.index < this.selection.from || this.selection.to < nextPosition.index){
          continue;
        }

        let direction = {
          x: 0,
          y: 0,
          z: 0,
        }
        let arrowLength = 0;
        let velocity = {
          x: nextPosition.x - position.x,
          y: nextPosition.y - position.y,
          z: nextPosition.z - position.z,
        }
        switch(this.vectorMode){
          case 'position':
            direction = position;
            break;
          case 'velocity':
            direction = velocity;
            break;
          case 'acceleration':
            direction = {
              x: velocity.x - previousVelocity.x,
              y: velocity.y - previousVelocity.y,
              z: velocity.z - previousVelocity.z,
            }
            break;
        }
        arrowLength = this.arrowLength*getNorm(direction)/(nextIndex - index);
        previousVelocity = direction;
       
        rtn.push({
          position: {
            x: position.x,
            y: position.y, 
            z: position.z
          },
          direction,
          length: arrowLength,
          color: this.colorValues ? this.getColor(this.colorValues[index]) : this.color
        })
      }

      return rtn;
    },
  },
}
</script>